import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '@/utils/api';
import { useReintentoTrasSesion } from '@/utils/sesion';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

// Mismos identificadores que usa el motor. El texto es el que ve el profesional.
const SINTOMAS = [
  ['symptom_chest_pain', 'Dolor o malestar en el pecho, en reposo o con actividad'],
  ['symptom_neck_jaw_arm_pain', 'Dolor o molestia en cuello, mandíbula o brazos'],
  ['symptom_dyspnea', 'Falta de aire inusual o con esfuerzos leves'],
  ['symptom_dizziness', 'Mareos o pérdida de conciencia durante la actividad'],
  ['symptom_orthopnea', 'Dificultad para respirar acostado, o disnea nocturna'],
  ['symptom_ankle_swelling', 'Hinchazón persistente en los tobillos'],
  ['symptom_palpitations', 'Palpitaciones o latidos irregulares frecuentes'],
  ['symptom_intermittent_claudication', 'Dolor en las piernas al caminar que mejora al parar'],
  ['symptom_heart_murmur', 'Soplo cardíaco informado'],
  ['symptom_unusual_fatigue', 'Fatiga inusual con actividades habituales'],
];

const ENFERMEDADES = [
  ['diabetes', 'Diabetes tipo 1 o 2'],
  ['prediabetes', 'Prediabetes o glucemia alterada'],
  ['cardiac_disease', 'Enfermedad cardíaca conocida'],
  ['cerebrovascular_disease', 'ACV o AIT'],
  ['peripheral_artery_disease', 'Enfermedad arterial periférica o aneurisma de aorta'],
  ['chronic_kidney_disease', 'Enfermedad renal crónica'],
  ['hypertension', 'Hipertensión arterial'],
  ['dyslipidemia', 'Dislipemia'],
];

const NIVELES = [
  ['sedentario', 'Sedentario', 'Prácticamente no realiza actividad física'],
  ['irregular', 'Insuficientemente activo', 'Hace actividad, pero no de forma sostenida'],
  ['activo_regular', 'Activo regular', 'Sostiene actividad de forma regular'],
];

/** Pregunta de sí/no que despliega su contenido solo cuando la respuesta es sí. */
function PreguntaDesplegable({ titulo, ayuda, valor, onChange, children }) {
  return (
    <div className="rounded-lg border border-slate-200 p-4 space-y-3">
      <div>
        <p className="font-medium text-slate-800">{titulo}</p>
        {ayuda && <p className="text-sm text-slate-500 mt-0.5">{ayuda}</p>}
      </div>
      <div className="flex gap-2">
        {[['no', 'No'], ['si', 'Sí']].map(([v, label]) => {
          const activo = (v === 'si') === valor;
          return (
            <button
              key={v}
              type="button"
              onClick={() => onChange(v === 'si')}
              className={`px-4 py-1.5 rounded-md text-sm border transition ${
                activo
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
      {valor && <div className="pt-1">{children}</div>}
    </div>
  );
}

export default function RetestPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [alumno, setAlumno] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const [f, setF] = useState({
    sesiones_por_semana: '',
    minutos_por_sesion: '',
    semanas_cumplidas: '',
    intensidad_percibida: '',
    activity_level: '',
    hubo_cambios_enfermedades: false,
    enfermedades: {},
    hubo_sintomas: false,
    sintomas: {},
    hubo_dolor_nuevo: false,
    dolor_descripcion: '',
    weight: '',
    blood_pressure_systolic: '',
    blood_pressure_diastolic: '',
    resting_heart_rate: '',
    observaciones: '',
  });

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    let cancelado = false;
    api.getAlumno(id)
      .then((a) => { if (!cancelado) setAlumno(a); })
      .catch((e) => { if (!cancelado) toast.error(e?.message || 'No se pudo cargar el alumno'); })
      .finally(() => { if (!cancelado) setCargando(false); });
    return () => { cancelado = true; };
  }, [id]);

  const minutosSemanales =
    (parseInt(f.sesiones_por_semana, 10) || 0) * (parseInt(f.minutos_por_sesion, 10) || 0);

  const guardar = async () => {
    if (!f.activity_level) {
      toast.error('Indicá el nivel de actividad física actual');
      return;
    }
    const num = (v) => (v === '' || v === null ? null : Number(v));
    const payload = {
      sesiones_por_semana: parseInt(f.sesiones_por_semana, 10) || 0,
      minutos_por_sesion: parseInt(f.minutos_por_sesion, 10) || 0,
      semanas_cumplidas: parseInt(f.semanas_cumplidas, 10) || 0,
      intensidad_percibida: f.intensidad_percibida || null,
      activity_level: f.activity_level,
      hubo_cambios_enfermedades: f.hubo_cambios_enfermedades,
      enfermedades: f.hubo_cambios_enfermedades ? f.enfermedades : null,
      hubo_sintomas: f.hubo_sintomas,
      sintomas: f.hubo_sintomas ? f.sintomas : null,
      hubo_dolor_nuevo: f.hubo_dolor_nuevo,
      dolor_descripcion: f.hubo_dolor_nuevo ? f.dolor_descripcion : null,
      weight: num(f.weight),
      blood_pressure_systolic: num(f.blood_pressure_systolic),
      blood_pressure_diastolic: num(f.blood_pressure_diastolic),
      resting_heart_rate: num(f.resting_heart_rate),
      observaciones: f.observaciones || null,
    };

    try {
      setGuardando(true);
      await api.createRetest(id, payload);
      toast.success('Retest registrado');
      navigate(`/alumnos/${id}`);
    } catch (e) {
      if (e?.status === 401) {
        marcarPendiente();
        toast.info('Tu sesión se venció. Ingresá de nuevo: lo cargado no se perdió y se guarda solo.');
        return;
      }
      toast.error(e?.message || 'No se pudo registrar el retest');
    } finally {
      setGuardando(false);
    }
  };

  const marcarPendiente = useReintentoTrasSesion(() => guardar());

  if (cargando) {
    return (
      <Layout>
        <div className="py-20 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <Link
            to={`/alumnos/${id}`}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-3"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a la ficha
          </Link>
          <h1 className="text-2xl font-bold text-gray-800 font-['Inter']">
            Retest {alumno ? `de ${alumno.nombre}` : ''}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            No hace falta repetir la entrevista completa. Lo que no se pregunta acá se
            toma de la entrevista anterior.
          </p>
        </div>

        {/* Adherencia */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-['Manrope'] text-slate-700">
              Qué hizo en este período
            </CardTitle>
            <CardDescription>
              Sirve para distinguir después si un resultado que no mejoró se debe al
              programa o a que no se cumplió.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ses">Sesiones por semana</Label>
                <Input id="ses" type="number" min={0} max={14}
                  value={f.sesiones_por_semana}
                  onChange={(e) => set('sesiones_por_semana', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="min">Minutos por sesión</Label>
                <Input id="min" type="number" min={0} max={300}
                  value={f.minutos_por_sesion}
                  onChange={(e) => set('minutos_por_sesion', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sem">Semanas que cumplió</Label>
                <Input id="sem" type="number" min={0} max={104}
                  value={f.semanas_cumplidas}
                  onChange={(e) => set('semanas_cumplidas', e.target.value)} />
              </div>
            </div>

            {minutosSemanales > 0 && (
              <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                Declara <strong>{minutosSemanales} minutos por semana</strong>.
                <span className="text-slate-500">
                  {' '}Como referencia, ACSM recomienda 150 minutos semanales de intensidad
                  moderada.
                </span>
              </p>
            )}

            <div className="space-y-1.5">
              <Label>Intensidad percibida</Label>
              <div className="flex flex-wrap gap-2">
                {['leve', 'moderada', 'vigorosa'].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => set('intensidad_percibida', v)}
                    className={`px-4 py-1.5 rounded-md text-sm border capitalize transition ${
                      f.intensidad_percibida === v
                        ? 'bg-slate-800 text-white border-slate-800'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Nivel de actividad — entrada del motor */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-['Manrope'] text-slate-700">
              Nivel de actividad física actual
            </CardTitle>
            <CardDescription>
              Lo definís vos a partir de lo declarado arriba. Es una de las tres entradas
              del motor, así que puede cambiar el escenario.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {NIVELES.map(([valor, titulo, ayuda]) => (
              <button
                key={valor}
                type="button"
                onClick={() => set('activity_level', valor)}
                className={`w-full text-left rounded-lg border px-4 py-3 transition ${
                  f.activity_level === valor
                    ? 'border-slate-800 bg-slate-50'
                    : 'border-slate-200 hover:border-slate-400'
                }`}
              >
                <p className="font-medium text-slate-800">{titulo}</p>
                <p className="text-sm text-slate-500">{ayuda}</p>
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Los tres cambios */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-['Manrope'] text-slate-700">
              Qué cambió desde la última entrevista
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <PreguntaDesplegable
              titulo="¿Hubo algún cambio en enfermedades cardíacas, metabólicas o renales?"
              ayuda="Un diagnóstico nuevo, o uno anterior que se confirmó."
              valor={f.hubo_cambios_enfermedades}
              onChange={(v) => set('hubo_cambios_enfermedades', v)}
            >
              <div className="space-y-2">
                {ENFERMEDADES.map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2.5 text-sm text-slate-700">
                    <Checkbox
                      checked={!!f.enfermedades[k]}
                      onCheckedChange={(c) =>
                        set('enfermedades', { ...f.enfermedades, [k]: !!c })
                      }
                    />
                    {label}
                  </label>
                ))}
                <p className="text-xs text-slate-500 pt-1">
                  Marcá solo lo que corresponde ahora. Lo que ya estaba declarado se
                  conserva aunque no lo marques.
                </p>
              </div>
            </PreguntaDesplegable>

            <PreguntaDesplegable
              titulo="¿Apareció algún signo o síntoma de los que evaluamos?"
              valor={f.hubo_sintomas}
              onChange={(v) => set('hubo_sintomas', v)}
            >
              <div className="space-y-2">
                {SINTOMAS.map(([k, label]) => (
                  <label key={k} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <Checkbox
                      className="mt-0.5"
                      checked={!!f.sintomas[k]}
                      onCheckedChange={(c) => set('sintomas', { ...f.sintomas, [k]: !!c })}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </PreguntaDesplegable>

            <PreguntaDesplegable
              titulo="¿Apareció algún dolor nuevo en estos meses?"
              valor={f.hubo_dolor_nuevo}
              onChange={(v) => set('hubo_dolor_nuevo', v)}
            >
              <Input
                placeholder="Zona y breve descripción"
                value={f.dolor_descripcion}
                onChange={(e) => set('dolor_descripcion', e.target.value)}
              />
            </PreguntaDesplegable>
          </CardContent>
        </Card>

        {/* Mediciones */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-['Manrope'] text-slate-700">
              Mediciones de hoy
            </CardTitle>
            <CardDescription>
              Si dejás un campo vacío se conserva el valor de la entrevista anterior.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="peso">Peso (kg)</Label>
                <Input id="peso" type="number" step="0.1" value={f.weight}
                  onChange={(e) => set('weight', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pas">PA sistólica</Label>
                <Input id="pas" type="number" value={f.blood_pressure_systolic}
                  onChange={(e) => set('blood_pressure_systolic', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pad">PA diastólica</Label>
                <Input id="pad" type="number" value={f.blood_pressure_diastolic}
                  onChange={(e) => set('blood_pressure_diastolic', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fc">FC de reposo</Label>
                <Input id="fc" type="number" value={f.resting_heart_rate}
                  onChange={(e) => set('resting_heart_rate', e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="obs">Observaciones</Label>
              <textarea
                id="obs"
                rows={3}
                value={f.observaciones}
                onChange={(e) => set('observaciones', e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="Opcional"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2 pb-4">
          <Button variant="outline" onClick={() => navigate(`/alumnos/${id}`)} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={guardar} disabled={guardando} className="gap-2">
            {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Registrar retest
          </Button>
        </div>
      </div>
    </Layout>
  );
}
